/** Icon set for the Terminal design — inline SVGs (no icon-lib dependency:
 *  SSR-safe, tree-shaken by construction, consistent 1.6px stroke). */

type P = { className?: string };
const base = {
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.6,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  'aria-hidden': true as const,
};

/** Brand mark — notched ticket with a rising price-trend line and two data
 *  dots (matches the official logo; keep in sync with public/og-card-3.png + favicon set). */
export const TicketLogo = ({ className }: P) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden>
    <path
      d="M4.4 6h15.2a2.2 2.2 0 0 1 2.2 2.2v2.2a1.6 1.6 0 0 0 0 3.2v2.2a2.2 2.2 0 0 1-2.2 2.2H4.4a2.2 2.2 0 0 1-2.2-2.2v-2.2a1.6 1.6 0 0 0 0-3.2V8.2A2.2 2.2 0 0 1 4.4 6Z"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinejoin="round"
    />
    <path
      d="M7 14.6l3-3.1 2 1.6 4.4-3.7"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <circle cx="8.4" cy="9.4" r="1" fill="currentColor" />
    <circle cx="15.1" cy="15" r="1" fill="currentColor" />
  </svg>
);

export const SearchIcon = ({ className }: P) => (
  <svg className={className} {...base}>
    <circle cx="11" cy="11" r="7" />
    <path d="M16.5 16.5 21 21" />
  </svg>
);

export const XIcon = ({ className }: P) => (
  <svg className={className} {...base}>
    <path d="M6 6l12 12M18 6L6 18" />
  </svg>
);

export const MapPinIcon = ({ className }: P) => (
  <svg className={className} {...base}>
    <path d="M12 21s-7-5.1-7-11a7 7 0 1 1 14 0c0 5.9-7 11-7 11Z" />
    <circle cx="12" cy="10" r="2.5" />
  </svg>
);

export const CalendarIcon = ({ className }: P) => (
  <svg className={className} {...base}>
    <rect x="3.5" y="5" width="17" height="16" rx="2" />
    <path d="M3.5 10h17M8 3v4M16 3v4" />
  </svg>
);

export const ClockIcon = ({ className }: P) => (
  <svg className={className} {...base}>
    <circle cx="12" cy="12" r="8.5" />
    <path d="M12 7.5V12l3 2" />
  </svg>
);

export const ChevronDownIcon = ({ className }: P) => (
  <svg className={className} {...base}>
    <path d="m6 9 6 6 6-6" />
  </svg>
);

export const ChevronLeftIcon = ({ className }: P) => (
  <svg className={className} {...base}>
    <path d="m14.5 6-6 6 6 6" />
  </svg>
);

export const ArrowUpRightIcon = ({ className }: P) => (
  <svg className={className} {...base}>
    <path d="M7 17 17 7M9 7h8v8" />
  </svg>
);

export const CheckIcon = ({ className }: P) => (
  <svg className={className} {...base} strokeWidth={2}>
    <path d="m5 12.5 4.5 4.5L19 7.5" />
  </svg>
);

export const BadgeCheckIcon = ({ className }: P) => (
  <svg className={className} {...base}>
    <path d="M12 2.8 14 4.6l2.7-.3.8 2.6 2.4 1.3-1 2.5 1 2.5-2.4 1.3-.8 2.6-2.7-.3-2 1.8-2-1.8-2.7.3-.8-2.6-2.4-1.3 1-2.5-1-2.5L6.5 6.9l.8-2.6 2.7.3Z" />
    <path d="m9.3 12 1.9 1.9 3.5-3.8" />
  </svg>
);

export const MoonStarIcon = ({ className }: P) => (
  <svg className={className} {...base}>
    <path d="M20 14.5A8.5 8.5 0 0 1 9.5 4a8.5 8.5 0 1 0 10.5 10.5Z" />
    <path d="M18 3v4M20 5h-4" strokeWidth={1.4} />
  </svg>
);

export const SunIcon = ({ className }: P) => (
  <svg className={className} {...base}>
    <circle cx="12" cy="12" r="4.2" />
    <path d="M12 2.5V5M12 19v2.5M2.5 12H5M19 12h2.5M4.9 4.9l1.8 1.8M17.3 17.3l1.8 1.8M19.1 4.9l-1.8 1.8M6.7 17.3l-1.8 1.8" />
  </svg>
);

export const MoonIcon = ({ className }: P) => (
  <svg className={className} {...base}>
    <path d="M20 14.5A8.5 8.5 0 0 1 9.5 4a8.5 8.5 0 1 0 10.5 10.5Z" />
  </svg>
);

export const ActivityIcon = ({ className }: P) => (
  <svg className={className} {...base}>
    <path d="M3 12h4l2.5-7 5 14 2.5-7h4" />
  </svg>
);

export const ShieldCheckIcon = ({ className }: P) => (
  <svg className={className} {...base}>
    <path d="M12 2.8 20 6v5.4c0 5-3.4 8.3-8 9.8-4.6-1.5-8-4.8-8-9.8V6Z" />
    <path d="m8.8 11.8 2.2 2.2 4.2-4.5" />
  </svg>
);

export const MusicIcon = ({ className }: P) => (
  <svg className={className} {...base}>
    <path d="M9 18.5V6l11-2v12.5" />
    <circle cx="6.5" cy="18.5" r="2.5" />
    <circle cx="17.5" cy="16.5" r="2.5" />
  </svg>
);

export const TrophyIcon = ({ className }: P) => (
  <svg className={className} {...base}>
    <path d="M8 4h8v6a4 4 0 0 1-8 0Z" />
    <path d="M8 5H4.5v2A3.5 3.5 0 0 0 8 10.5M16 5h3.5v2A3.5 3.5 0 0 1 16 10.5M12 14v4M8.5 20.5h7M12 18v2.5" />
  </svg>
);

export const MicIcon = ({ className }: P) => (
  <svg className={className} {...base}>
    <rect x="9" y="2.8" width="6" height="11.5" rx="3" />
    <path d="M5.5 11.5a6.5 6.5 0 0 0 13 0M12 18v3.2M8.5 21.2h7" />
  </svg>
);

export const DramaIcon = ({ className }: P) => (
  <svg className={className} {...base}>
    <path d="M4 4.5h9v7a4.5 4.5 0 0 1-9 0Z" />
    <path d="M11 9.5h9v7a4.5 4.5 0 0 1-9 0v-7ZM6.2 7.7h.7M10 7.7h.7M6.6 9.9c.6.6 2 .6 2.6 0" />
    <path d="M13.5 12.7h.7M17.3 12.7h.7M14 15.7c.6-.6 2-.6 2.6 0" strokeWidth={1.4} />
  </svg>
);

export const LayersIcon = ({ className }: P) => (
  <svg className={className} {...base}>
    <path d="m12 3 9 4.5L12 12 3 7.5Z" />
    <path d="m3 12.5 9 4.5 9-4.5M3 17l9 4.5L21 17" strokeWidth={1.4} />
  </svg>
);

export const LineChartIcon = ({ className }: P) => (
  <svg className={className} {...base}>
    <path d="M4 4v16h16" />
    <path d="m7.5 14.5 3.5-4 3 2.5 4.5-6" />
  </svg>
);

export const InfoIcon = ({ className }: P) => (
  <svg className={className} {...base}>
    <circle cx="12" cy="12" r="8.5" />
    <path d="M12 11v5M12 7.8v.4" strokeWidth={2} />
  </svg>
);

export const PlusIcon = ({ className }: P) => (
  <svg className={className} {...base} strokeWidth={2}>
    <path d="M12 5v14M5 12h14" />
  </svg>
);

export const MenuIcon = ({ className }: P) => (
  <svg className={className} {...base}>
    <path d="M4 7h16M4 12h16M4 17h16" />
  </svg>
);
